import type { ReviewOptionTone } from '@/features/deal-review/review-options'
import type { DealLeadStatusTone } from '@/features/deal-review/deal-lead-views'

// Kept apart from the component: a module exporting both breaks fast refresh.
export const triggerToneClasses: Record<ReviewOptionTone, string> = {
  neutral: 'border-primary/15 bg-transparent text-primary',
  positive:
    'border-green-700/25 bg-green-50 text-green-800 focus:ring-green-700/30',
  negative:
    'border-destructive/25 bg-destructive/5 text-destructive focus:ring-destructive/30',
  warning:
    'border-amber-700/25 bg-amber-50 text-amber-900 focus:ring-amber-700/30',
}

// Colour repeated under data-[placeholder]: Radix paints an empty trigger as a placeholder.
export const quietToneClasses: Record<
  ReviewOptionTone,
  { trigger: string; dot: string }
> = {
  neutral: {
    trigger:
      'border-transparent text-primary/70 data-[placeholder]:text-primary/70 hover:border-primary/15',
    dot: 'bg-primary/30',
  },
  positive: {
    trigger:
      'border-transparent text-green-800 data-[placeholder]:text-green-800 hover:border-green-700/25',
    dot: 'bg-green-600',
  },
  negative: {
    trigger:
      'border-transparent text-destructive data-[placeholder]:text-destructive hover:border-destructive/25',
    dot: 'bg-destructive',
  },
  warning: {
    trigger:
      'border-transparent text-amber-900 data-[placeholder]:text-amber-900 hover:border-amber-700/25',
    dot: 'bg-amber-600',
  },
}

export const recordedFillClasses: Record<ReviewOptionTone, string> = {
  neutral: 'bg-primary/5 hover:bg-primary/10 hover:border-transparent',
  positive: 'bg-green-50 hover:bg-green-100 hover:border-transparent',
  negative: 'bg-destructive/5 hover:bg-destructive/10 hover:border-transparent',
  warning: 'bg-amber-50 hover:bg-amber-100 hover:border-transparent',
}

export const suggestedToneClasses: Record<
  ReviewOptionTone,
  { trigger: string; icon: string }
> = {
  neutral: {
    trigger:
      'border-dashed border-primary/30 text-primary/70 data-[placeholder]:text-primary/70 hover:border-primary/50',
    icon: 'text-primary/50',
  },
  positive: {
    trigger:
      'border-dashed border-green-700/40 text-green-800 data-[placeholder]:text-green-800 hover:border-green-700/60',
    icon: 'text-green-700',
  },
  negative: {
    trigger:
      'border-dashed border-destructive/40 text-destructive data-[placeholder]:text-destructive hover:border-destructive/60',
    icon: 'text-destructive',
  },
  warning: {
    trigger:
      'border-dashed border-amber-700/40 text-amber-900 data-[placeholder]:text-amber-900 hover:border-amber-700/60',
    icon: 'text-amber-700',
  },
}

export const itemToneClasses: Record<ReviewOptionTone, string> = {
  neutral:
    'text-primary hover:bg-primary/5 focus:bg-primary/5 data-[state=checked]:bg-primary/5',
  positive:
    'text-green-800 hover:bg-green-50 focus:bg-green-50 data-[state=checked]:bg-green-50',
  negative:
    'text-destructive hover:bg-destructive/5 focus:bg-destructive/5 data-[state=checked]:bg-destructive/5',
  warning:
    'text-amber-900 hover:bg-amber-50 focus:bg-amber-50 data-[state=checked]:bg-amber-50',
}

// 70% is the first step of --primary on white that clears 4.5:1 contrast.
export const MUTED_TEXT = 'text-primary/70'

export const STATUS_TONE_CLASSES: Record<DealLeadStatusTone, string> = {
  neutral: 'border-primary/20 bg-primary/5 text-primary',
  positive: 'border-green-700/25 bg-green-50 text-green-800',
  negative: 'border-destructive/25 bg-destructive/5 text-destructive',
  warning: 'border-amber-700/25 bg-amber-50 text-amber-900',
}

export const FILE_ACTION_CLASSES =
  'h-7.5 shrink-0 gap-1.25 rounded-md px-2.5 text-primary [&_svg]:size-3'

// Header status pills (Outlook intake, DealCloud send) share these tones.
export type PillTone = 'default' | 'busy' | 'warning'

export const PILL_TONE: Record<PillTone, string> = {
  default: 'border-border bg-background text-foreground hover:bg-muted/60',
  busy: 'border-sky-700/20 bg-sky-50 text-sky-900 hover:bg-sky-100',
  warning: 'border-amber-700/25 bg-amber-50 text-amber-900 hover:bg-amber-100',
}

export const PANEL_ACTION =
  'h-5 shrink-0 gap-1 px-1.5 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3'
